/**
 * Created by neo on 23.08.21.
 */
import { makeAutoObservable, toJS } from 'mobx';
import { v4 as UUID } from 'uuid';
import { LocalizedValue, createLocalizedValue } from '../../../LocalizedValue';
import { MesoCycleTemplate, createMesoCycleTemplate } from './MesoCycleTemplate';
import { HttpBackend } from '../../../../Services/Http/HttpBackend';
import { Media, createMedia } from '../../../Media/Media';
import { LocalizedEntity, createLocalizedEntity } from '../../../LocalizedEntity';
import { MicroCycle, createMicroCycle } from '../MicroCycle';
import { EquipmentConfiguration } from '../EquipmentConfiguration';
import { ProgramSchedule } from '../../ProgramSchedule';
import { ConditionalMedia, createConditionalMedia } from '../../SuperMacro/ConditionalMedia';
import { ProgramTemplateProgramParams, createProgramTemplateProgramParams } from '../ProgramTemplateProgramParams';
import { ProgramTrackingParameter, createProgramTrackingParameter } from '../ProgramTrackingParameter';
import { ProgramTemplateResult, createProgramTemplateResult } from '../../ProgramTemplateResult';
import CacheService from '../../../../Services/CacheService';
import { HOURS_12 } from '../../../../Utils/Constants';
import { FitnessProgramTemplateQueryRequest } from './FitnessProgramTemplateQueryRequest';
import { ProgramActiveResponse, createProgramActiveResponse } from '../../ProgramActiveResponse';
import { TimesOfDay } from '../../TimesOfDay';
import { TimeRange, createTimeRange } from '../../../TimeRange';
import { TimeOfDay } from '../../../TimeOfDay';

export const EQUIPMENT_VALUE_FREE = 0;
export const EQUIPMENT_VALUE_CUSTOM = 1;
export const EQUIPMENT_VALUE_GYM = 2;
export const LEVEL_VALUE_BEGINNER = 0;
export const LEVEL_VALUE_INTERMEDIATE = 1;
export const LEVEL_VALUE_ADVANCED = 2;

export type CreateFitnessProgramRequest = {
  coachProgramTemplateId: string;
  tags: string[];
  equipmentConfiguration: Partial<EquipmentConfiguration>;
  schedule: Partial<ProgramSchedule>;
};

export type FitnessProgramTemplate = LocalizedEntity & {
  includedTags: string[];
  excludedTags: string[];
  templateName?: string;
  name: LocalizedValue[];
  description: LocalizedValue[];
  mesoCycles: MesoCycleTemplate[];
  medias: ConditionalMedia[];
  image?: Media;
  videos: Media[];
  params?: ProgramTemplateProgramParams;
  minWorkoutDurationInMinutes?: number;
  maxWorkoutDurationInMinutes?: number;
  archived: boolean;
  expectedResults: ProgramTemplateResult[];
  requiredInputs: ProgramTrackingParameter[];
  athleteTrackingParameters: ProgramTrackingParameter[];
  defaultTimesOfDay: TimesOfDay;
  readonly minDaysPerWeek: number;
  readonly maxDaysPerWeek: number;
  readonly microCyclesCount: number;
  readonly equipmentValue: number;
  readonly levelValue: number;
  readonly highestLevelValue: number;
  readonly requiredNonFreeEquipment: string[];
  readonly needsGym: boolean;
  readonly canUseGym: boolean;
  readonly canCustomEquipment: boolean;
  readonly needsEquipment: boolean;
  readonly equipmentLocked: boolean;
  readonly doesNotNeedEquipment: boolean;
  readonly canChooseLocation: boolean;
  getImage(tags?: string[]): Media | undefined;
  getVideo(tags?: string[]): Media | undefined;
  create(params?: Partial<CreateFitnessProgramRequest>): Promise<MicroCycle>;
};

export function createFitnessProgramTemplate(json?: Partial<FitnessProgramTemplate>): FitnessProgramTemplate {
  const base = createLocalizedEntity(json as any);
  return makeAutoObservable({
    ...base,
    includedTags: json?.includedTags ?? [],
    excludedTags: json?.excludedTags ?? [],
    templateName: json?.templateName,
    name: ((json?.name as any) ?? []).map((l: any) => createLocalizedValue(l)),
    description: ((json?.description as any) ?? []).map((l: any) => createLocalizedValue(l)),
    mesoCycles: ((json?.mesoCycles as any) ?? []).map((m: any) => createMesoCycleTemplate(m)),
    medias: ((json?.medias as any) ?? []).map((m: any) => createConditionalMedia(m)),
    image: json?.image ? createMedia(json.image as any) : undefined,
    videos: ((json?.videos as any) ?? []).map((v: any) => createMedia(v)),
    params: json?.params ? createProgramTemplateProgramParams(json.params as any) : undefined,
    minWorkoutDurationInMinutes: json?.minWorkoutDurationInMinutes,
    maxWorkoutDurationInMinutes: json?.maxWorkoutDurationInMinutes,
    archived: json?.archived ?? false,
    expectedResults: ((json?.expectedResults as any) ?? []).map((l: any) => createProgramTemplateResult(l)),
    requiredInputs: ((json?.requiredInputs as any) ?? []).map((r: any) => createProgramTrackingParameter(r)),
    athleteTrackingParameters: ((json?.athleteTrackingParameters as any) ?? []).map((r: any) =>
      createProgramTrackingParameter(r),
    ),
    defaultTimesOfDay: Object.entries((json?.defaultTimesOfDay as any) ?? ({} as TimesOfDay))
      .map(([key, value]) => [key, createTimeRange(value as any)])
      .reduce((obj, [key, value]) => Object.assign(obj, { [key as TimeOfDay]: value }), {} as TimesOfDay),

    get minDaysPerWeek(): number {
      return this.mesoCycles.reduce((min, meso) => Math.min(min, meso.workoutDays.length), Number.MAX_SAFE_INTEGER);
    },

    get maxDaysPerWeek(): number {
      return this.mesoCycles.reduce((min, meso) => Math.max(min, meso.workoutDays.length), 0);
    },

    get microCyclesCount(): number {
      return this.mesoCycles.reduce((min, meso) => min + meso.cycles, 0);
    },

    get equipmentValue(): number {
      if (this.includedTags.includes('equipment:no')) {
        return 0;
      } else if (this.includedTags.includes('equipment:custom')) {
        return 1;
      } else if (this.includedTags.includes('equipment:gym')) {
        return 2;
      } else if (this.params?.equipmentConfiguration?.isFree) {
        return 0;
      } else if (!this.params || this.params.equipmentConfiguration.isGym) {
        return 2;
      } else if (this.params.equipmentConfiguration.isCustom) {
        return 1;
      }
      return 2;
    },

    get levelValue(): number {
      if (this.includedTags.includes('level:beginner')) {
        return 0;
      } else if (this.includedTags.includes('level:intermediate')) {
        return 1;
      } else if (this.includedTags.includes('level:advanced')) {
        return 2;
      }
      return 0;
    },

    get highestLevelValue(): number {
      if (this.includedTags.includes('level:advanced')) {
        return 2;
      } else if (this.includedTags.includes('level:intermediate')) {
        return 1;
      } else if (this.includedTags.includes('level:beginner')) {
        return 0;
      }
      return 0;
    },

    get requiredNonFreeEquipment(): string[] {
      return this.params?.equipmentConfiguration.equipmentTypes.filter((e) => e !== 'FREE' && e !== 'FREE_OUTDOOR') ?? [];
    },

    get needsGym(): boolean {
      return this.canUseGym && this.requiredNonFreeEquipment.length === 0;
    },

    get canUseGym(): boolean {
      return this.includedTags.includes('equipment:gym');
    },

    get canCustomEquipment(): boolean {
      return this.includedTags.includes('equipment:custom') && !this.params?.equipmentLocked;
    },

    get needsEquipment(): boolean {
      return (
        !this.includedTags.includes('equipment:no') &&
        (this.needsGym || this.canCustomEquipment || this.requiredNonFreeEquipment.length > 0)
      );
    },

    get equipmentLocked(): boolean {
      return this.requiredNonFreeEquipment.length > 0 && !!this.params?.equipmentLocked;
    },

    get doesNotNeedEquipment(): boolean {
      return this.includedTags.includes('equipment:no');
    },

    get canChooseLocation(): boolean {
      return (
        this.includedTags.filter((t) => t === 'equipment:no' || t === 'equipment:custom' || t === 'equipment:gym')
          .length > 1
      );
    },

    toJS(newId: boolean = false) {
      return {
        ...(function() { const { id, createdAt, updatedAt, createdBy, updatedBy, name, description } = base; return { id: newId ? UUID() : id, createdAt, updatedAt, createdBy, updatedBy, name, description }; })(),
        includedTags: this.includedTags,
        excludedTags: this.excludedTags,
        templateName: this.templateName,
        name: this.name.map((n) => n),
        description: this.description.map((n) => n),
        mesoCycles: this.mesoCycles.map((m) => m.toJS(newId)),
        medias: this.medias.map((m) => m),
        image: this.image,
        videos: this.videos.map((v) => v),
        params: this.params,
        archived: this.archived,
        expectedResults: this.expectedResults.map((l) => l),
        requiredInputs: this.requiredInputs.map((r) => r),
        athleteTrackingParameters: this.athleteTrackingParameters.map((r) => r),
        defaultTimesOfDay: Array.from(Object.entries(this.defaultTimesOfDay))
          .map(([key, value]) => [key, value])
          .reduce((agg, [key, entries]) => Object.assign(agg, { [key as string]: entries }), {}),
      };
    },

    getImage(tags: string[] = []): Media | undefined {
      const imageMedia = this.medias.filter((m) => m.mediaType.startsWith('image'));
      // Try matching with tags first
      const tagged = imageMedia
        .filter((m) => m.matches(tags))
        .sort((a, b) => b.matchCount(tags) - a.matchCount(tags));
      if (tagged.length > 0) return tagged[0];
      // Fallback: any image regardless of tags
      if (imageMedia.length > 0) return imageMedia[0];
      // Last resort: first media of any type
      return this.medias[0];
    },

    getVideo(tags: string[] = []): Media | undefined {
      return this.medias
        .slice()
        .filter((m) => m.mediaType.startsWith('video'))
        .filter((m) => m.matches(tags))
        .sort((a, b) => b.matchCount(tags) - a.matchCount(tags))[0];
    },

    create(params?: Partial<CreateFitnessProgramRequest>): Promise<MicroCycle> {
      return HttpBackend.post(
        `/coach/program`,
        Object.assign(toJS(params ?? {}), { coachProgramTemplateId: this.id }),
      ).then((res) => createMicroCycle(res));
    },
  });
}

export const FitnessProgramTemplateApi = {
  find(request?: Partial<FitnessProgramTemplateQueryRequest>): Promise<FitnessProgramTemplate[]> {
    return HttpBackend.get(`/coach/program/template`, toJS(request)).then((res) =>
      res.map((r: any) => createFitnessProgramTemplate(r)),
    );
  },

  findOne(id: string): Promise<FitnessProgramTemplate> {
    return CacheService.cache(
      `fitnessProgramTemplate:${id}`,
      () => HttpBackend.get(`/coach/program/template/${id}`),
      (res) => createFitnessProgramTemplate(res),
      HOURS_12,
    ).then((res) => createFitnessProgramTemplate(res));
  },

  count(request?: Partial<FitnessProgramTemplateQueryRequest>): Promise<number> {
    return HttpBackend.get(`/coach/program/template/count`, toJS(request)).then((res) => res ?? 0);
  },

  isActive(id: string): Promise<ProgramActiveResponse> {
    return HttpBackend.get(`/coach/program/template/${id}/isActive`).then((res) => createProgramActiveResponse(res));
  },
};
